// HistorieView.tsx — #historie: per-fund analysis/backtesting page. Cache-only (GET
// /api/funds/:isin/history — lib/api.ts's getFundHistory, the frozen contract in
// lib/types.ts's FundHistoryResponse), so this view never touches the simulate/backtest
// endpoints — it's a standalone read of whatever is already cached for one fund, same
// "cache-only, honest, never fabricate" spirit as the Daten view's coverage cards. All
// the actual math lives in lib/fundAnalytics.ts (independently unit-tested); this file is
// selection state + wiring + presentation.
import { useEffect, useMemo, useState } from 'preact/hooks';
import { ApiError, getFundHistory, getFunds } from '../lib/api.js';
import type { FundHistoryResponse, FundInfo } from '../lib/types.js';
import {
  addYearsIso,
  ageByYear,
  annualizedVolatility,
  availableWindowYears,
  cagr,
  calendarYearReturns,
  distributionsByYear,
  distributionYieldTtm,
  drawdownSeries,
  growthOf,
  rollingAnnualizedReturns,
  sliceByWindow,
  totalReturnGrowth,
} from '../lib/fundAnalytics.js';
import { loadLastHistorieIsin, saveLastHistorieIsin } from '../lib/historieStorage.js';
import { Hint } from '../components/Hint.js';
import { formatCents, formatCentsCompact, formatDateDe, formatEur, formatEurCompact, formatPercent } from '../lib/format.js';
import { hexToRgba, readCssVar } from '../lib/theme.js';
import { HistorieChart } from '../components/HistorieChart.js';
import type { HistorieSeriesSpec } from '../components/HistorieChart.js';
import { HistorieBarChart } from '../components/HistorieBarChart.js';
import type { HistorieBar } from '../components/HistorieBarChart.js';

type ViewKind = 'kurs' | 'wachstum' | 'drawdown' | 'rolling' | 'kalenderjahr' | 'ausschuettungen' | 'age';
type RangeKey = '1J' | '3J' | '5J' | '10J' | 'Max';

const RANGE_YEARS: Record<Exclude<RangeKey, 'Max'>, number> = { '1J': 1, '3J': 3, '5J': 5, '10J': 10 };
const RANGE_ORDER: RangeKey[] = ['1J', '3J', '5J', '10J', 'Max'];
const ROLLING_YEAR_OPTIONS = [1, 3, 5, 10] as const;
/** "Wachstum 10.000 €" — a fixed, round reference amount (the task spec's own label),
 * not user-editable: this page is analysis, not a new backtest input form. */
const GROWTH_AMOUNT_CENTS = 1_000_000;

function toUnixSeconds(iso: string): number {
  return Math.floor(new Date(`${iso}T00:00:00Z`).getTime() / 1000);
}

type FundsState =
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'success'; funds: FundInfo[] };

type HistoryState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'success'; data: FundHistoryResponse };

export function HistorieView() {
  const [fundsState, setFundsState] = useState<FundsState>({ status: 'loading' });
  const [selectedIsin, setSelectedIsin] = useState<string | null>(() => loadLastHistorieIsin());
  const [historyState, setHistoryState] = useState<HistoryState>({ status: 'idle' });

  const [activeView, setActiveView] = useState<ViewKind>('kurs');
  const [range, setRange] = useState<RangeKey>('Max');
  const [wachstumMode, setWachstumMode] = useState<'kurs' | 'total'>('kurs');
  const [rollingYears, setRollingYears] = useState<number>(5);

  useEffect(() => {
    let cancelled = false;
    getFunds()
      .then((res) => {
        if (cancelled) return;
        setFundsState({ status: 'success', funds: res.funds });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFundsState({
          status: 'error',
          error: err instanceof ApiError ? err.message : 'Unbekannter Fehler beim Laden der Fonds.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const enabledFunds = fundsState.status === 'success' ? fundsState.funds.filter((f) => f.enabled) : [];

  // Defaults/repairs the selection once funds are known: keep the remembered ISIN if
  // it's still a valid, enabled fund; otherwise fall back to the first enabled one.
  useEffect(() => {
    if (fundsState.status !== 'success') return;
    const enabled = fundsState.funds.filter((f) => f.enabled);
    if (enabled.length === 0) {
      setSelectedIsin(null);
      return;
    }
    setSelectedIsin((current) => (current && enabled.some((f) => f.isin === current) ? current : enabled[0]!.isin));
  }, [fundsState]);

  useEffect(() => {
    if (!selectedIsin) {
      setHistoryState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setHistoryState({ status: 'loading' });
    getFundHistory(selectedIsin)
      .then((data) => {
        if (cancelled) return;
        setHistoryState({ status: 'success', data });
        saveLastHistorieIsin(selectedIsin);
        // A newly loaded fund may not support the previously active view/range/holding
        // period (a dist-only view on an acc fund, a shorter history) — reset to safe
        // defaults instead of showing a stale, possibly-invalid selection.
        setActiveView('kurs');
        setRange('Max');
        setWachstumMode('kurs');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setHistoryState({
          status: 'error',
          error: err instanceof ApiError ? err.message : 'Unbekannter Fehler beim Laden.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [selectedIsin]);

  const history = historyState.status === 'success' ? historyState.data : null;
  const prices = history?.prices ?? [];

  const availableRangeYears = useMemo(() => availableWindowYears(prices, [1, 3, 5, 10]), [prices]);
  const availableRollingYears = useMemo(
    () => availableWindowYears(prices, [...ROLLING_YEAR_OPTIONS]),
    [prices],
  );

  // Clamp range/rollingYears down whenever the fund (and so its available windows)
  // changes, so a stale "10J" selection never silently renders an empty chart.
  useEffect(() => {
    if (range !== 'Max' && !availableRangeYears.includes(RANGE_YEARS[range])) setRange('Max');
  }, [availableRangeYears, range]);
  useEffect(() => {
    if (availableRollingYears.length > 0 && !availableRollingYears.includes(rollingYears)) {
      setRollingYears(availableRollingYears[availableRollingYears.length - 1]!);
    }
  }, [availableRollingYears, rollingYears]);

  const rangeFromDate = useMemo(() => {
    if (range === 'Max' || prices.length === 0) return undefined;
    const lastDate = prices[prices.length - 1]!.date;
    return addYearsIso(lastDate, -RANGE_YEARS[range]);
  }, [range, prices]);

  const rangeChips = useMemo(
    () => RANGE_ORDER.filter((r) => r === 'Max' || availableRangeYears.includes(RANGE_YEARS[r])),
    [availableRangeYears],
  );

  const stats = useMemo(() => {
    if (!history || prices.length === 0) return null;
    const lastDate = prices[prices.length - 1]!.date;
    const cagrSinceInception = cagr(prices);
    const cagrWindows = availableWindowYears(prices, [1, 3, 5, 10]).map((years) => ({
      years,
      value: cagr(prices, addYearsIso(lastDate, -years)),
    }));
    const vol = annualizedVolatility(prices);
    const { maxDrawdown } = drawdownSeries(prices);
    const fullYears = calendarYearReturns(prices).filter((y) => !y.partial);
    const best = fullYears.length > 0 ? fullYears.reduce((a, b) => (b.return > a.return ? b : a)) : null;
    const worst = fullYears.length > 0 ? fullYears.reduce((a, b) => (b.return < a.return ? b : a)) : null;
    const ttmYield =
      history.kind === 'dist' ? distributionYieldTtm(history.distributions, history.lastCloseEur) : null;
    return { cagrSinceInception, cagrWindows, vol, maxDrawdown, best, worst, ttmYield };
  }, [history, prices]);

  const viewChips = useMemo((): { key: ViewKind; label: string }[] => {
    if (!history) return [];
    const chips: { key: ViewKind; label: string }[] = [
      { key: 'kurs', label: 'Kurs' },
      { key: 'wachstum', label: 'Wachstum 10.000 €' },
      { key: 'drawdown', label: 'Drawdown' },
      { key: 'rolling', label: 'Rollierende Rendite p.a.' },
      { key: 'kalenderjahr', label: 'Kalenderjahr-Renditen' },
    ];
    if (history.kind === 'dist') chips.push({ key: 'ausschuettungen', label: 'Ausschüttungen/Jahr' });
    if (history.meldefonds) chips.push({ key: 'age', label: 'Ausschüttungsgleiche Erträge/Jahr' });
    return chips;
  }, [history]);

  function renderKurs(h: FundHistoryResponse) {
    const sliced = sliceByWindow(prices, rangeFromDate);
    const x = sliced.map((p) => toUnixSeconds(p.date));
    const closes = sliced.map((p): number | null => p.closeEur);
    const accent = readCssVar('--accent', '#60a5fa');
    const specs: HistorieSeriesSpec[] = [
      {
        label: 'Kurs',
        color: accent,
        fill: hexToRgba(accent, 0.1),
        valueFmt: (v) => (v == null ? '—' : formatEur(v)),
      },
    ];
    const markerXs =
      h.kind === 'dist'
        ? h.distributions.filter((d) => !rangeFromDate || d.exDate >= rangeFromDate).map((d) => toUnixSeconds(d.exDate))
        : [];
    return (
      <>
        <HistorieChart
          x={x}
          series={[closes]}
          specs={specs}
          yTickFmt={(v) => formatEurCompact(v)}
          markerXs={markerXs}
          markerColor="#fbbf24"
          emptyMessage="Noch keine Kursdaten — im Daten-Tab aktualisieren."
        />
        {markerXs.length > 0 ? (
          <Hint>
            <p class="field-hint">Punktierte Linien: Ausschüttungen (Ex-Tag) — {markerXs.length} im gewählten Zeitraum.</p>
          </Hint>
        ) : null}
      </>
    );
  }

  function renderWachstum(h: FundHistoryResponse) {
    const isDist = h.kind === 'dist';
    const useTotal = isDist && wachstumMode === 'total';
    const points = useTotal
      ? totalReturnGrowth(GROWTH_AMOUNT_CENTS, prices, h.distributions, rangeFromDate)
      : growthOf(GROWTH_AMOUNT_CENTS, prices, rangeFromDate);
    const x = points.map((p) => toUnixSeconds(p.date));
    const values = points.map((p): number | null => p.valueCents);
    const accent = readCssVar('--accent', '#60a5fa');
    const specs: HistorieSeriesSpec[] = [
      {
        label: useTotal ? 'inkl. Ausschüttungen (brutto)' : 'Kurs',
        color: accent,
        fill: hexToRgba(accent, 0.12),
        valueFmt: (v) => (v == null ? '—' : formatCents(v)),
      },
    ];
    return (
      <>
        {isDist ? (
          <div class="segmented segmented-scroll" role="group" aria-label="Wachstums-Basis" style={{ marginBottom: '0.6rem' }}>
            <button type="button" class="chip" aria-pressed={wachstumMode === 'kurs'} onClick={() => setWachstumMode('kurs')}>
              Kurs
            </button>
            <button
              type="button"
              class="chip"
              aria-pressed={wachstumMode === 'total'}
              onClick={() => setWachstumMode('total')}
            >
              inkl. Ausschüttungen (brutto reinvestiert)
            </button>
          </div>
        ) : null}
        <HistorieChart
          x={x}
          series={[values]}
          specs={specs}
          yTickFmt={(v) => formatCentsCompact(v)}
          emptyMessage="Noch keine Kursdaten — im Daten-Tab aktualisieren."
        />
        {useTotal ? (
          <p class="field-hint">
            Brutto/vor Steuern: Ausschüttungen werden am Ex-Tag zum Schlusskurs reinvestiert angenommen, ohne
            KESt-Abzug. Bei thesaurierenden Fonds ist der Kurs selbst bereits eine Total-Return-Größe.
          </p>
        ) : null}
      </>
    );
  }

  function renderDrawdown() {
    const sliced = sliceByWindow(prices, rangeFromDate);
    const { series } = drawdownSeries(sliced);
    const x = series.map((p) => toUnixSeconds(p.date));
    const values = series.map((p): number | null => p.value);
    const negative = readCssVar('--negative', '#f87171');
    const specs: HistorieSeriesSpec[] = [
      {
        label: 'Drawdown',
        color: negative,
        fill: hexToRgba(negative, 0.18),
        valueFmt: (v) => (v == null ? '—' : formatPercent(v)),
      },
    ];
    return (
      <HistorieChart
        x={x}
        series={[values]}
        specs={specs}
        yTickFmt={(v) => formatPercent(v)}
        showZeroLine
        emptyMessage="Noch keine Kursdaten — im Daten-Tab aktualisieren."
      />
    );
  }

  function renderRolling() {
    if (availableRollingYears.length === 0) {
      return (
        <div class="empty-state">
          Noch nicht genug Kursdaten für ein rollierendes Mehrjahres-Fenster — mindestens ein volles Jahr Historie
          wird benötigt.
        </div>
      );
    }
    const { series, best, worst, median } = rollingAnnualizedReturns(prices, rollingYears);
    const x = series.map((p) => toUnixSeconds(p.date));
    const values = series.map((p): number | null => p.annualizedReturn);
    const accent = readCssVar('--accent', '#60a5fa');
    const positive = readCssVar('--positive', '#4ade80');
    const negative = readCssVar('--negative', '#f87171');
    const seriesData: (number | null)[][] = [values];
    const specs: HistorieSeriesSpec[] = [
      {
        label: `${rollingYears}J p.a.`,
        color: accent,
        fill: hexToRgba(accent, 0.1),
        valueFmt: (v) => (v == null ? '—' : formatPercent(v)),
      },
    ];
    if (best != null) {
      seriesData.push(x.map(() => best));
      specs.push({
        label: 'Bestes Fenster',
        color: positive,
        width: 1,
        dash: [4, 3],
        valueFmt: (v) => (v == null ? '—' : formatPercent(v)),
      });
    }
    if (worst != null) {
      seriesData.push(x.map(() => worst));
      specs.push({
        label: 'Schlechtestes Fenster',
        color: negative,
        width: 1,
        dash: [4, 3],
        valueFmt: (v) => (v == null ? '—' : formatPercent(v)),
      });
    }
    if (median != null) {
      seriesData.push(x.map(() => median));
      specs.push({
        label: 'Median',
        color: '#93a0b8',
        width: 1,
        dash: [2, 3],
        valueFmt: (v) => (v == null ? '—' : formatPercent(v)),
      });
    }
    return (
      <>
        <div class="segmented" role="group" aria-label="Haltedauer" style={{ marginBottom: '0.6rem' }}>
          {availableRollingYears.map((y) => (
            <button key={y} type="button" class="chip" aria-pressed={rollingYears === y} onClick={() => setRollingYears(y)}>
              {y}J
            </button>
          ))}
        </div>
        <HistorieChart x={x} series={seriesData} specs={specs} yTickFmt={(v) => formatPercent(v)} showZeroLine />
      </>
    );
  }

  function renderKalenderjahr() {
    const years = calendarYearReturns(prices);
    const bars: HistorieBar[] = years.map((y) => ({
      key: String(y.year),
      label: y.partial ? `${y.year}*` : String(y.year),
      value: y.return,
    }));
    const hasPartial = years.some((y) => y.partial);
    return (
      <>
        <HistorieBarChart
          bars={bars}
          mode="signed"
          valueFmt={(v) => formatPercent(v)}
          emptyMessage="Noch keine Kursdaten — im Daten-Tab aktualisieren."
        />
        {hasPartial ? (
          <p class="field-hint">
            * unvollständiges Jahr (Auflage bzw. laufendes Jahr noch nicht abgeschlossen) — nicht in
            „bestes/schlechtestes Kalenderjahr" oben berücksichtigt.
          </p>
        ) : null}
      </>
    );
  }

  function renderAusschuettungen(h: FundHistoryResponse) {
    const years = distributionsByYear(h.distributions);
    const bars: HistorieBar[] = years.map((y) => ({ key: String(y.year), label: String(y.year), value: y.totalPerUnitEur }));
    return (
      <>
        <HistorieBarChart bars={bars} mode="accent" valueFmt={(v) => formatEur(v)} emptyMessage="Noch keine Ausschüttungen im Cache." />
        {stats?.ttmYield != null ? (
          <p class="field-hint">Ausschüttungsrendite (TTM, brutto): {formatPercent(stats.ttmYield)}.</p>
        ) : null}
      </>
    );
  }

  function renderAge(h: FundHistoryResponse) {
    const years = ageByYear(h.age);
    const bars: HistorieBar[] = years.map((y) => ({ key: String(y.year), label: String(y.year), value: y.totalPerUnitEur }));
    return (
      <>
        <div class="banner banner-info" role="status" style={{ marginBottom: '0.6rem' }}>
          <strong>Was ist eine OeKB-Meldung?</strong>
          <span>
            Ein Meldefonds meldet seine steuerlich relevanten Kennzahlen — u. a. die ausschüttungsgleichen Erträge
            (AGE) — einmal pro Geschäftsjahr an die OeKB (Österreichische Kontrollbank). Nur mit diesen Meldedaten
            lässt sich die AGE-Historie hier korrekt nachbilden; ohne Meldung bleibt dieser Fonds für diesen Zweck
            eine Lücke.
          </span>
        </div>
        {bars.length === 0 ? (
          <div class="empty-state">
            Für diesen Fonds liegen noch keine OeKB-Meldungen im Cache vor — AGE-Backtesting benötigt mindestens
            eine Meldung pro Geschäftsjahr.
          </div>
        ) : (
          <HistorieBarChart bars={bars} mode="accent" valueFmt={(v) => formatEur(v)} />
        )}
      </>
    );
  }

  return (
    <div>
      <div class="card">
        <h2>Historie</h2>
        {fundsState.status === 'loading' ? (
          <div class="empty-state">
            <span class="spinner" /> Fonds werden geladen…
          </div>
        ) : fundsState.status === 'error' ? (
          <div class="banner banner-error" role="alert">
            <strong>Fonds konnten nicht geladen werden</strong>
            <span>{fundsState.error}</span>
          </div>
        ) : enabledFunds.length === 0 ? (
          <div class="empty-state">Noch keine Fonds hinterlegt — im Daten-Tab aktualisieren oder einen Fonds anlegen.</div>
        ) : (
          <>
            <div class="field" style={{ marginBottom: history ? '0.6rem' : 0 }}>
              <label for="historie-fund-select">Fonds</label>
              <select
                id="historie-fund-select"
                value={selectedIsin ?? ''}
                onChange={(e) => setSelectedIsin((e.currentTarget as HTMLSelectElement).value || null)}
              >
                {enabledFunds.map((f) => (
                  <option key={f.isin} value={f.isin}>
                    {f.name} ({f.isin})
                  </option>
                ))}
              </select>
            </div>
            {history ? (
              <div class="faint" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem 1rem' }}>
                <span style={{ color: 'var(--text)', fontWeight: 700 }}>{history.name}</span>
                <span>{history.isin}</span>
                <span>{history.kind === 'dist' ? 'Ausschüttend' : 'Thesaurierend'}{history.meldefonds ? ' · Meldefonds' : ''}</span>
                <span>Auflage: {formatDateDe(history.inceptionDate)}</span>
                <span>
                  Kurs: {history.lastCloseEur != null ? formatEur(history.lastCloseEur) : '—'} ({history.currency})
                </span>
              </div>
            ) : null}
          </>
        )}
      </div>

      {historyState.status === 'loading' ? (
        <div class="card">
          <div class="empty-state">
            <span class="spinner" /> Wird geladen…
          </div>
        </div>
      ) : null}

      {historyState.status === 'error' ? (
        <div class="card">
          <div class="banner banner-error" role="alert">
            <strong>Historie konnte nicht geladen werden</strong>
            <span>{historyState.error}</span>
          </div>
        </div>
      ) : null}

      {history && prices.length === 0 ? (
        <div class="card">
          <div class="empty-state">Noch keine Kursdaten — im Daten-Tab aktualisieren.</div>
        </div>
      ) : null}

      {history && stats && prices.length > 0 ? (
        <div class="card">
          <div class="summary-grid">
            <Tile label="CAGR seit Auflage" value={stats.cagrSinceInception != null ? formatPercent(stats.cagrSinceInception) : '—'} />
            {stats.cagrWindows.map((w) => (
              <Tile key={w.years} label={`CAGR ${w.years}J`} value={w.value != null ? formatPercent(w.value) : '—'} />
            ))}
            <Tile label="Annualisierte Volatilität" value={stats.vol != null ? formatPercent(stats.vol) : '—'} />
            <Tile label="Max. Drawdown" value={formatPercent(stats.maxDrawdown)} negative={stats.maxDrawdown < 0} />
            <Tile
              label="Bestes Kalenderjahr"
              value={stats.best ? `${stats.best.year}: ${formatPercent(stats.best.return)}` : '—'}
            />
            <Tile
              label="Schlechtestes Kalenderjahr"
              value={stats.worst ? `${stats.worst.year}: ${formatPercent(stats.worst.return)}` : '—'}
              negative={!!stats.worst && stats.worst.return < 0}
            />
            <Tile label="Aktueller Kurs" value={history.lastCloseEur != null ? formatEur(history.lastCloseEur) : '—'} />
            <Tile label="Auflagedatum" value={formatDateDe(history.inceptionDate)} />
            {history.kind === 'dist' ? (
              <Tile label="Ausschüttungsrendite (TTM)" value={stats.ttmYield != null ? formatPercent(stats.ttmYield) : '—'} />
            ) : null}
          </div>
        </div>
      ) : null}

      {history && prices.length > 0 ? (
        <div class="card">
          <div class="segmented segmented-scroll" role="group" aria-label="Ansicht" style={{ marginBottom: '0.6rem' }}>
            {viewChips.map((c) => (
              <button key={c.key} type="button" class="chip" aria-pressed={activeView === c.key} onClick={() => setActiveView(c.key)}>
                {c.label}
              </button>
            ))}
          </div>

          {activeView === 'kurs' || activeView === 'wachstum' || activeView === 'drawdown' ? (
            <div class="segmented" role="group" aria-label="Zeitraum" style={{ marginBottom: '0.6rem' }}>
              {rangeChips.map((r) => (
                <button key={r} type="button" class="chip" aria-pressed={range === r} onClick={() => setRange(r)}>
                  {r}
                </button>
              ))}
            </div>
          ) : null}

          {activeView === 'kurs' ? renderKurs(history) : null}
          {activeView === 'wachstum' ? renderWachstum(history) : null}
          {activeView === 'drawdown' ? renderDrawdown() : null}
          {activeView === 'rolling' ? renderRolling() : null}
          {activeView === 'kalenderjahr' ? renderKalenderjahr() : null}
          {activeView === 'ausschuettungen' ? renderAusschuettungen(history) : null}
          {activeView === 'age' ? renderAge(history) : null}
        </div>
      ) : null}
    </div>
  );
}

function Tile({ label, value, negative }: { label: string; value: string; negative?: boolean }) {
  return (
    <div class="summary-tile">
      <div class="label">{label}</div>
      <div class={`value${negative ? ' negative' : ''}`}>{value}</div>
    </div>
  );
}
